<template>
  <div>
    <base-header class="pb-6" type="gradient-gray">
      <b-row align-v="center" class="py-4">
        <b-col lg="6" cols="7">
          <h6 class="h2 text-white d-inline-block mb-0">
            <i class="fa fa-file-alt mr-2"></i>
            View Document
          </h6>

          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <b-breadcrumb class="breadcrumb-links">
              <b-breadcrumb-item to="/">Home</b-breadcrumb-item>
              <b-breadcrumb-item :to="`/companies/${companyId}`"
                >Company</b-breadcrumb-item
              >
              <b-breadcrumb-item :to="`/companies/${companyId}/documents`"
                >Documents</b-breadcrumb-item
              >
              <b-breadcrumb-item
                :to="`/companies/${companyId}/documents/${documentId}`"
                >View</b-breadcrumb-item
              >
            </b-breadcrumb>
          </nav>
        </b-col>
      </b-row>
    </base-header>
    <b-container fluid class="mt--6">
      <loading
        :active.sync="loading"
        :can-cancel="false"
        :is-full-page="true"
      ></loading>

      <b-row class="starter-page" v-if="!loading && document">
        <b-col cols="12" md="6">
          <b-card>
            <b-card-header>
              <h1 class="h3">
                <i class="fa fa-building mr-2 text-muted"></i>
                {{ document.shortType | humanize }}
                <b-badge v-if="document.isFiled" variant="success" pill>
                  Filed
                  {{ document.filingDate | moment("MMM DD YYYY") }}
                </b-badge>
                <b-badge v-else-if="!document.isFiled" variant="warning" pill>
                  Not filed (due
                  {{ document.filingDueDate | moment("from", "now") }})
                </b-badge>
              </h1>
            </b-card-header>

            <b-list-group flush>
              <b-list-group-item>
                <b-row>
                  <b-col sm="4">
                    <i class="fa fa-calendar mr-2 text-muted fa-fw"></i>
                    <span v-if="document.isFiled">
                      Filed on
                      {{ document.filingDate | moment("MMM DD YYYY") }}
                    </span>
                    <span v-else>
                      File by
                      {{ document.filingDueDate | moment("MMM DD YYYY") }}
                    </span>
                  </b-col>

                  <b-col sm="8">
                    <document-filing-date-widget
                      :account-id="account.id"
                      :company-id="document.companyId"
                      :document-id="document.id"
                      :document-filing-date="document.filingDate"
                      @update-filing-date="handleFilingDateUpdated"
                    ></document-filing-date-widget>
                  </b-col>
                </b-row>
              </b-list-group-item>

              <b-list-group-item>
                <contact-edit-form
                  :account-id="account.id"
                  :modal-id="
                    `edit-contact-${document.id}-${document.signatory.id}`
                  "
                  :contact="document.signatory"
                  @update-contact="document.signatory = $event"
                ></contact-edit-form>

                <i class="fa fa-signature mr-2 text-muted fa-fw"></i>
                Signed by
                <span
                  class="clickable"
                  v-b-modal="
                    `edit-contact-${document.id}-${document.signatory.id}`
                  "
                  title="Edit contact"
                  v-b-tooltip.hover
                  >{{ document.signatory.name | decode }}</span
                >
                ({{ document.details.signatoryRole }})
              </b-list-group-item>
            </b-list-group>

            <b-card-footer class="text-right">
              <b-btn
                variant="default"
                :to="`/companies/${companyId}/documents/${document.id}/edit`"
              >
                <i class="fa fa-edit mr-2"></i> Update document details
              </b-btn>
            </b-card-footer>
          </b-card>
        </b-col>
        <b-col cols="12" md="6">
          <b-card>
            <b-card-header>
              <h2 class="h3">
                <i class="fa fa-copy mr-2 text-muted"></i>
                Files
              </h2>
            </b-card-header>

            <b-list-group flush>
              <b-list-group-item v-if="!document.files.length"
                >No files yet</b-list-group-item
              >
              <b-list-group-item
                v-for="file in files"
                :key="file.id"
                :href="file.downloadUrl || '#'"
                :disabled="!file.downloadUrl"
                class="d-flex justify-content-between align-items-center"
              >
                <span>
                  <i v-if="file.type === 'docx'" class="fa fa-file-word"></i>
                  <i v-if="file.type === 'pdf'" class="fa fa-file-pdf"></i>
                  <span class="text-uppercase ml-2">{{ file.type }}</span>

                  <code class="small text-muted ml-2">
                    {{ file.id | slice(-4) }}-{{ file.version | slice(-4) }}
                  </code>
                </span>

                <span v-if="file.status === 'generating'">
                  <i class="fa fa-spin fa-sync mr-2"></i> Generating
                  file&hellip;
                </span>
                <span v-else-if="file.status === 'error'">
                  <i class="fa fa-exclamation-triangle mr-2"></i>
                  Error generating file
                </span>
                <span v-else-if="file.status === 'available'">
                  <i class="fa fa-download mr-2"></i>
                  Download
                </span>
              </b-list-group-item>
            </b-list-group>

            <b-card-footer class="text-right">
              <!-- FUTURE: show / highlight this when document version changes or document is based on outdated version of company -->
              <b-btn
                @click="regenerateFiles"
                :disabled="regeneratingFiles"
                v-if="!document.isFiled"
              >
                <span v-if="regeneratingFiles">
                  <i class="fa fa-sync fa-spin mr-2"></i> Regenerating&hellip;
                </span>
                <span v-else>
                  <i class="fa fa-redo mr-2"></i> Regenerate files
                </span>
              </b-btn>
            </b-card-footer>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>
<script>
import _ from "underscore";
import { get } from "dot-prop";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import DocumentFilingDateWidget from "./DocumentFilingDateWidget";
import ContactEditForm from "../../Contacts/ContactEditForm";

export default {
  name: "document-view",
  components: {
    RouteBreadCrumb,
    Loading,
    DocumentFilingDateWidget,
    ContactEditForm
  },
  props: {
    companyId: {
      type: String,
      description: "Company ID",
      required: true
    },
    documentId: {
      type: String,
      description: "Document ID",
      required: true
    }
  },
  data() {
    return {
      document: null,
      loading: false,
      errorMessage: null,
      regeneratingFiles: false
    };
  },
  computed: {
    account: function() {
      return this.$store.state.account;
    },
    files: function() {
      if (this.document && this.document.files) {
        return _.where(this.document.files, { version: this.document.version });
      } else {
        return [];
      }
    }
  },
  mounted() {
    this.loadDocument();
  },
  methods: {
    loadDocument(setLoadingState = true) {
      if (setLoadingState) {
        this.loading = true;
      }
      this.axios
        .get(
          `/v1/accounts/${this.account.id}/companies/${this.companyId}/documents/${this.documentId}`
        )
        .then(response => {
          if (setLoadingState) {
            this.loading = false;
          }
          this.document = response.data;

          this.reloadWhileFilesGenerating();
        })
        .catch(error => {
          if (setLoadingState) {
            this.loading = false;
          }
          this.errorMessage = "Failed to load document";
          if (get(error, "response.data.message")) {
            this.errorMessage = error.response.data.message;
          }
        });
    },
    reloadWhileFilesGenerating() {
      const DELAY_MS = 3000; // 3 seconds
      if (this.document) {
        const filesGenerating = _.findWhere(this.document.files, {
          status: "generating"
        });

        if (filesGenerating && this.$route.name === "View Document") {
          setTimeout(() => {
            this.loadDocument(false);
          }, DELAY_MS);
        }
      }
    },
    regenerateFiles() {
      this.regeneratingFiles = true;

      this.axios
        .put(
          `/v1/accounts/${this.account.id}/companies/${this.companyId}/documents/${this.documentId}/files`
        )
        .then(response => {
          this.regeneratingFiles = false;
          this.document.files = this.document.files.concat(response.data);

          this.reloadWhileFilesGenerating();
        })
        .catch(error => {
          this.regeneratingFiles = false;
          this.errorMessage = "Failed to load files";
          if (get(error, "response.data.message")) {
            this.errorMessage = error.response.data.message;
          }
        });
    },
    handleFilingDateUpdated(document) {
      this.document.filingDate = document.filingDate;
      this.document.isFiled = document.isFiled;
    }
  }
};
</script>
<style>
.starter-page {
  min-height: calc(100vh - 380px);
}
</style>
